import { ReactNode, useEffect, useLayoutEffect, useRef } from "react"
import cn from "classnames"
import { useOrientation, useWindowSize } from "react-use"
import debounce from "lodash.debounce"

import { MediaRenderer, VoximplantSdk } from "modules/Call/sdk"
import { Text } from "ui-kit/components-branded/index"
import { VideoUserName } from "modules/Call/components/VideoUserName"
import { logSdkEvent } from "modules/Call/VoximplantLogger"

interface VoxMediaPlayerProps {
    mediaRenderer?: MediaRenderer
    className?: string
    name?: string
    avatar?: ReactNode
}

function getVideoContainerSize(div: HTMLDivElement): [number, number] {
    const r = div.getBoundingClientRect()
    return [Math.round(r.width) * devicePixelRatio, Math.round(r.height) * devicePixelRatio]
}

const requestVideoSize = debounce((div: HTMLDivElement | null, mediaRenderer?: MediaRenderer) => {
    if (!div) return
    if (!mediaRenderer || mediaRenderer?.kind !== VoximplantSdk.MediaRendererKind.Video) return
    void mediaRenderer.requestVideoSize(...getVideoContainerSize(div))
}, 300)

export function VoxMediaPlayer({ mediaRenderer, className, name, avatar }: VoxMediaPlayerProps) {
    const videoContainer = useRef<HTMLDivElement>(null)

    useLayoutEffect(() => {
        const videoContainerElement = videoContainer.current
        if (!mediaRenderer || !videoContainerElement) return

        if (mediaRenderer.kind !== VoximplantSdk.MediaRendererKind.Video) return

        try {
            mediaRenderer.render(videoContainerElement)
            requestVideoSize(videoContainerElement, mediaRenderer)
        } catch (err) {
            logSdkEvent(`💥 mediaRenderer.render() failed: ${String(err)}`)
        }

        return () => {
            try {
                mediaRenderer.clear()
            } catch (err) {
                logSdkEvent(`⚠️ mediaRenderer.clear() failed: ${String(err)}`)
            }
        }
    }, [mediaRenderer])

    const windowSize = useWindowSize()
    const orientation = useOrientation()

    useEffect(() => {
        requestVideoSize(videoContainer.current, mediaRenderer)
    }, [windowSize.width, orientation.type])

    return (
        <>
            {name && (
                <VideoUserName>
                    <Text isSecondary>{name}</Text>
                </VideoUserName>
            )}
            <div ref={videoContainer} className={cn("video-container", className)} />
            {avatar}
        </>
    )
}
